import { Component, OnInit, ViewChild } from '@angular/core';

import { Step1Component } from './wizard/step1/step1.component';
import { Step2SearchComponent } from './wizard/step2-search/step2-search.component';
import { Step3UpsellComponent } from './wizard/step3-upsell/step3-upsell.component';
import { Step4XsellComponent } from './wizard/step4-xsell/step4-xsell.component';
import { Step5CartComponent } from './wizard/step5-cart/step5-cart.component';
import { Step6PaymentComponent } from './wizard/step6-payment/step6-payment.component';
import { ParticlesComponent } from './components/particles/particles.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FinishComponent } from './components/finish/finish.component';
import { ApiService } from './service/api.service';
import { Product } from './components/products/products.component';
import { CartService } from './service/cart.service';
import { Step7TicketComponent } from './wizard/step7-ticket/step7-ticket.component';
import { FlowCrossSellingResponse, FlowUpsellingResponse } from './service/api.dto';
import { RECOMMENDATIONS_SERVER_ERROR_MESSAGE, RecommendationsFlowService } from './wizard/services/recommendations-flow.service';
import { SelectedProductsService } from './wizard/services/selected-products.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [FormsModule, CommonModule, Step1Component, Step2SearchComponent, Step3UpsellComponent, Step4XsellComponent, Step5CartComponent, Step6PaymentComponent, Step7TicketComponent, ParticlesComponent, FinishComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'client';
  step = 1;
  loadingEnvironment = true;
  loadingText = false;
  envError?: string;
  public logoUrl: string = '';
  public welcomeText: string = 'Presiona la pantalla para comenzar';
  @ViewChild('upsellComponent', { static: false }) set upsellComponentRef(component: Step3UpsellComponent | undefined) {
    this.upsellComponent = component;
  }
  upsellComponent: Step3UpsellComponent | undefined;

  @ViewChild('xsellComponent', { static: false }) set xsellComponentRef(component: Step4XsellComponent | undefined) {
    this.xsellComponent = component;
    if (component && this.crossSellResponse?.crosssellingProducts?.productos && this.crossSellResponse.crosssellingProducts.productos.length > 0) {
      component.setProducts(this.crossSellResponse);
    }
  }
  xsellComponent: Step4XsellComponent | undefined;
  constructor(
    private apiService: ApiService, 
    private cartService: CartService,
    private recoFlow: RecommendationsFlowService,
    private selectionService: SelectedProductsService
  ) { }

  ngOnInit(): void {
    const posID = window.location.hash.split('#')[1];
    if(!posID) {
      this.loadingText = false;
      this.envError = 'No se encontró el POS';
      return;
    }

    const posMemory = sessionStorage.getItem('posID');
    if(posMemory != posID) {
      this.loadingText = true;
      this.apiService.getPosData(posID).subscribe((data: any) => {
        console.log('POS DATA', data);
        this.apiService.details = data;
        sessionStorage.setItem('posID', posID);
        sessionStorage.setItem('posDetails', JSON.stringify(this.apiService.details));
        
        this.applyBodyStyle();
      });
    } else {
      this.apiService.details = JSON.parse(sessionStorage.getItem('posDetails') || '{}');
      console.log('Loaded details', this.apiService.details);
      this.applyBodyStyle();
    }
  }

  applyBodyStyle() {
    this.logoUrl = this.apiService.details!.logo_url;
    this.welcomeText = this.apiService.details!.welcome_text || 'Presiona la pantalla para comenzar';
    window.document.body.style.background = this.apiService.details!.background;
    document.body.style.setProperty('--primary-color', this.apiService.details!.primary_color);

    // Generar active-background basado en el background principal
    const activeBackground = this.generateActiveBackground(this.apiService.details!.background);
    document.body.style.setProperty('--active-background', activeBackground);

    this.apiService.resetConversation();
    this.loadingEnvironment = false;
  }

  private generateActiveBackground(background: string): string {
    // console.log('Processing background:', background);

    // Si es un gradiente lineal
    const linearGradientMatch = background.match(/linear-gradient\((\d+)deg,\s*(.+)\)/);
    if (linearGradientMatch) {
      const originalDeg = parseInt(linearGradientMatch[1]);
      const colorsString = linearGradientMatch[2];
      // console.log('Linear gradient detected - deg:', originalDeg, 'colors:', colorsString);

      // Invertir el ángulo (sumar 180 grados)
      const newDeg = (originalDeg + 180) % 360;

      // Procesar los colores para oscurecerlos
      const colors = colorsString.split(',').map(color => {
        const trimmedColor = color.trim();
        const darkened = this.darkenColor(trimmedColor);
        // console.log('Color:', trimmedColor, '->', darkened);
        return darkened;
      });

      const result = `linear-gradient(${newDeg}deg, ${colors.join(', ')})`;
      // console.log('Generated linear gradient:', result);
      return result;
    }

    // Si es un gradiente radial
    const radialGradientMatch = background.match(/radial-gradient\((.+)\)/);
    if (radialGradientMatch) {
      const colorsString = radialGradientMatch[1];
      // console.log('Radial gradient detected - colors:', colorsString);

      // Procesar los colores para oscurecerlos y invertir el orden
      const colors = colorsString.split(',').map(color => {
        const trimmedColor = color.trim();
        const darkened = this.darkenColor(trimmedColor);
        // console.log('Color:', trimmedColor, '->', darkened);
        return darkened;
      }).reverse();

      const result = `radial-gradient(${colors.join(', ')})`;
      // console.log('Generated radial gradient:', result);
      return result;
    }

    // Si es un color sólido
    const result = this.darkenColor(background);
    // console.log('Solid color:', background, '->', result);
    return result;
  }

  private darkenColor(color: string): string {
    // console.log('Darkening color:', color);

    // Remover espacios y convertir a minúsculas
    const cleanColor = color.trim().toLowerCase();
    // console.log('Clean color:', cleanColor);

    // Si es un color hexadecimal
    if (cleanColor.match(/^#[0-9a-f]{6}$/)) {
      const result = this.darkenHexColor(cleanColor);
      // console.log('Hex 6 result:', result);
      return result;
    }

    // Si es un color hexadecimal con 3 dígitos
    if (cleanColor.match(/^#[0-9a-f]{3}$/)) {
      const expanded = '#' + cleanColor[1] + cleanColor[1] + cleanColor[2] + cleanColor[2] + cleanColor[3] + cleanColor[3];
      const result = this.darkenHexColor(expanded);
      // console.log('Hex 3 result:', result);
      return result;
    }

    // Si es rgb o rgba
    const rgbMatch = cleanColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      const alpha = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;

      const darkenedR = Math.max(0, Math.floor(r * 0.6));
      const darkenedG = Math.max(0, Math.floor(g * 0.6));
      const darkenedB = Math.max(0, Math.floor(b * 0.6));

      let result;
      if (alpha < 1) {
        result = `rgba(${darkenedR}, ${darkenedG}, ${darkenedB}, ${alpha})`;
      } else {
        result = `rgb(${darkenedR}, ${darkenedG}, ${darkenedB})`;
      }
      // console.log('RGB result:', result);
      return result;
    }

    // Si es hsl o hsla
    const hslMatch = cleanColor.match(/hsla?\((\d+),\s*(\d+)%,\s*(\d+)%(?:,\s*([\d.]+))?\)/);
    if (hslMatch) {
      const h = parseInt(hslMatch[1]);
      const s = parseInt(hslMatch[2]);
      const l = parseInt(hslMatch[3]);
      const alpha = hslMatch[4] ? parseFloat(hslMatch[4]) : 1;

      // Reducir la luminosidad para oscurecer
      const darkenedL = Math.max(0, Math.floor(l * 0.6));

      let result;
      if (alpha < 1) {
        result = `hsla(${h}, ${s}%, ${darkenedL}%, ${alpha})`;
      } else {
        result = `hsl(${h}, ${s}%, ${darkenedL}%)`;
      }
      // console.log('HSL result:', result);
      return result;
    }

    // Si es un color con porcentaje (ej: #8922e5 0%)
    const colorWithPercentMatch = cleanColor.match(/^([#\w]+)\s+(\d+)%$/);
    if (colorWithPercentMatch) {
      const baseColor = colorWithPercentMatch[1];
      const percent = colorWithPercentMatch[2];
      const darkenedBase = this.darkenColor(baseColor);
      const result = `${darkenedBase} ${percent}%`;
      // console.log('Color with percent result:', result);
      return result;
    }

    // Si no se puede procesar, devolver el color original
    // console.log('Could not process color, returning original:', color);
    return color;
  }

  private darkenHexColor(hex: string): string {
    // console.log('Darkening hex color:', hex);

    // Convertir hex a RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // console.log('RGB values:', r, g, b);

    // Oscurecer multiplicando por 0.6 (más agresivo)
    const darkenedR = Math.max(0, Math.floor(r * 0.5));
    const darkenedG = Math.max(0, Math.floor(g * 0.5));
    const darkenedB = Math.max(0, Math.floor(b * 0.5));

    // console.log('Darkened RGB values:', darkenedR, darkenedG, darkenedB);

    // Convertir de vuelta a hex
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    const result = `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
    // console.log('Final hex result:', result);
    return result;
  }

  onNext() {
    this.step++;
  }

  onAddMore() {
    this.step = 2;
  }

  tapScreen() {
    if(this.step == 1) {
      this.step++;
      document.body.classList.add('bgMove');
    }
  }

  reloadExperience() {
    // Resetear la conversación con el backend
    this.apiService.resetConversation();
    
    // Limpiar el carrito
    this.cartService.clearCart();
    
    // Resetear el estado del servicio de recomendaciones (upsell y cross-sell)
    this.recoFlow.resetState();
    this.selectionService.clear();
    
    // Limpiar la respuesta de cross-selling guardada
    this.crossSellResponse = null;
    
    // Remover la clase bgMove del body si está presente
    document.body.classList.remove('bgMove');
    
    // Volver al paso 1
    this.step = 1;
  }
  crossSellResponse: FlowCrossSellingResponse | null = null;

  onProductSelected(product: Product | null, step: number) {

    const posDetails = this.apiService.details;
    if (!posDetails) {
      console.warn('POS details not available');
      return;
    }
    
    const upsellingEnabled = !!posDetails?.upselling_enabled;
    const crossSellingEnabled = !!posDetails?.cross_selling_enabled;

    const integratedFlow = this.apiService.integratedRecommendationsFlow;
    if (integratedFlow && (step === 2 || step === 3 || step === 4)) {
      // En el flujo integrado los productos se sincronizan al completar cada etapa
      return;
    }

    if(step == 2) {
      let recommendationErrorShown = false;
      const notifyRecommendationServerError = () => {
        if (!recommendationErrorShown) {
          recommendationErrorShown = true;
          // Error silenciado - no se muestra alert
        }
      };

      if (product) {
        this.addToCart(product);
      }

      if (integratedFlow) {
        this.step = 5;
        return;
      }

      if (upsellingEnabled) {
        this.apiService.getUpsellingProducts(posDetails.catalog.id, posDetails.id, product?.productID || '').subscribe({
          next: (data: FlowUpsellingResponse) => {
            // console.log('Up-Sell RESPONSE', data);
            if(data.upsellingProducts.producto && this.upsellComponent) {
              this.upsellComponent.setProducts(data);
            } else if (crossSellingEnabled) {
              this.step = 4;
              if (this.crossSellResponse?.crosssellingProducts.productos && this.crossSellResponse.crosssellingProducts.productos.length > 0 && this.xsellComponent) {
                this.xsellComponent.setProducts(this.crossSellResponse);
              }
            } else {
              this.step = 5;
            }
          },
          error: (error) => {
            console.error('Error al obtener upselling', error);
            if (error?.status === 500) {
              notifyRecommendationServerError();
            }
          }
        });
      }

      if (crossSellingEnabled) {
        // En el flujo legacy no tenemos categorías de la recomendación,
        // por lo que enviamos solo los parámetros básicos.
        this.apiService.getCrossSellingProducts(posDetails.catalog.id, posDetails.id, product?.productID || '').subscribe({
          next: (data: FlowCrossSellingResponse) => {
            // console.log('Cross-Sell RESPONSE', data);
            this.crossSellResponse = data;
            if (data.crosssellingProducts.productos && data.crosssellingProducts.productos.length > 0) {
              if (!upsellingEnabled && this.xsellComponent) {
                this.xsellComponent.setProducts(data);
              }
            } else if (!upsellingEnabled) {
              this.step = 5;
            } else if (this.step >= 4) {
              this.step = 5;
            }
          },
          error: (error) => {
            console.error('Error al obtener cross-selling', error);
            if (error?.status === 500) {
              notifyRecommendationServerError();
            }
          }
        });
      } else {
        this.crossSellResponse = null;
      }

      if (upsellingEnabled) {
        this.step = 3;
      } else if (crossSellingEnabled) {
        this.step = 4;
      } else {
        this.step = 5;
      }

    } else if(step == 3) {
      // console.log('REPLACING PRODUCT', product);
      if (product) {
        this.replaceLastProduct(product);
      }
      if(crossSellingEnabled && this.crossSellResponse?.crosssellingProducts.productos && this.crossSellResponse.crosssellingProducts.productos.length > 0) {
        setTimeout(() => {
          this.xsellComponent?.setProducts(this.crossSellResponse!);
        }, 150);
        this.step = 4;
      } else {
        this.step = 5;
      }
    } else if(step == 4) {
      // console.log('ADDING PRODUCT TO CART', product);
      if (product) {
        this.addToCart(product);
      }
      this.step = 5;
      return;
    }
  }

  onStageCompleted(stepNumber: number) {
    if (!this.apiService.integratedRecommendationsFlow) {
      return;
    }

    const posDetails = this.apiService.details;
    if (!posDetails) {
      return;
    }

    if (stepNumber === 2) {
      if (posDetails.upselling_enabled) {
        this.step = 3;
      } else if (posDetails.cross_selling_enabled) {
        this.step = 4;
      } else {
        this.commitIntegratedSelectionsToCart();
        this.step = 5;
      }
      return;
    }

    if (stepNumber === 3) {
      if (posDetails.cross_selling_enabled) {
        this.step = 4;
      } else {
        this.commitIntegratedSelectionsToCart();
        this.step = 5;
      }
      return;
    }

    if (stepNumber === 4) {
      this.commitIntegratedSelectionsToCart();
      this.step = 5;
    }
  }

  addToCart(product: Product) {
    // console.log('Producto agregado al carrito:', product);
    this.cartService.addToCart({
      id: product.productID,
      name: product.name + (product.suffix || ''),
      image: product.image,
      price: product.price,
      quantity: product.quantity || 1
    });
  }

  replaceLastProduct(product: Product) {
    // console.log('Replacing last product', product);
    this.cartService.replaceLastItem({
      id: product.productID,
      name: product.name + (product.suffix || ''),
      image: product.image,
      price: product.price,
      quantity: product.quantity || 1
    });
  }

  private commitIntegratedSelectionsToCart() {
    const selections = this.selectionService.getSelectionsSnapshot();
    if (!selections.length) {
      return;
    }
    selections.forEach(entry => {
      this.addToCart(entry.product);
    });
    this.selectionService.clear();
  }


  onError(error: string) {
    // Error silenciado - no se muestra alert ni pantalla de error
    console.error('Error capturado:', error);
  }
}
